import { create } from 'zustand';

interface UiState {
  quickAddOpen: boolean;
  quickAddDefaultProjectId: string | null;
  openQuickAdd: (defaultProjectId?: string | null) => void;
  closeQuickAdd: () => void;

  taskDetailId: string | null;
  openTaskDetail: (taskId: string) => void;
  closeTaskDetail: () => void;

  projectFormOpen: boolean;
  projectFormId: string | null;
  openProjectForm: (projectId?: string | null) => void;
  closeProjectForm: () => void;

  searchOpen: boolean;
  openSearch: () => void;
  closeSearch: () => void;

  courseAddOpen: boolean;
  openCourseAdd: () => void;
  closeCourseAdd: () => void;

  habitFormOpen: boolean;
  habitFormId: string | null;
  openHabitForm: (habitId?: string | null) => void;
  closeHabitForm: () => void;

  habitStatsOpenFor: string | null;
  openHabitStats: (habitId: string) => void;
  closeHabitStats: () => void;

  catalogFormOpen: boolean;
  catalogFormId: string | null;
  openCatalogForm: (itemId?: string | null) => void;
  closeCatalogForm: () => void;

  carrefourCookiesOpen: boolean;
  openCarrefourCookies: () => void;
  closeCarrefourCookies: () => void;

  lafourcheCredentialsOpen: boolean;
  openLafourcheCredentials: () => void;
  closeLafourcheCredentials: () => void;


  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;

  settingsOpen: boolean;
  openSettings: () => void;
  closeSettings: () => void;

  notificationsOpen: boolean;
  openNotifications: () => void;
  closeNotifications: () => void;
}

/**
 * Une modale ou un panneau est ouvert. Les raccourcis d'une *page* (Escape qui revient en
 * arrière, par exemple) doivent s'effacer devant eux : les écouteurs sont tous posés sur
 * window, donc sans ce test un Escape ferme la modale **et** déclenche le raccourci dessous.
 * Un nouvel overlay s'ajoute ici, une fois — pas dans chaque page qui écoute le clavier.
 */
export function anyOverlayOpen(s: UiState): boolean {
  return (
    s.quickAddOpen ||
    s.taskDetailId !== null ||
    s.projectFormOpen ||
    s.searchOpen ||
    s.courseAddOpen ||
    s.habitFormOpen ||
    s.habitStatsOpenFor !== null ||
    s.catalogFormOpen ||
    s.carrefourCookiesOpen ||
    s.lafourcheCredentialsOpen ||
    s.drawerOpen ||
    s.settingsOpen ||
    s.notificationsOpen
  );
}

export const useUi = create<UiState>((set) => ({
  quickAddOpen: false,
  quickAddDefaultProjectId: null,
  openQuickAdd: (defaultProjectId = null) =>
    set({ quickAddOpen: true, quickAddDefaultProjectId: defaultProjectId }),
  closeQuickAdd: () => set({ quickAddOpen: false }),

  taskDetailId: null,
  openTaskDetail: (taskId) => set({ taskDetailId: taskId }),
  closeTaskDetail: () => set({ taskDetailId: null }),

  projectFormOpen: false,
  projectFormId: null,
  openProjectForm: (projectId = null) =>
    set({ projectFormOpen: true, projectFormId: projectId }),
  closeProjectForm: () => set({ projectFormOpen: false, projectFormId: null }),

  searchOpen: false,
  openSearch: () => set({ searchOpen: true }),
  closeSearch: () => set({ searchOpen: false }),

  courseAddOpen: false,
  openCourseAdd: () => set({ courseAddOpen: true }),
  closeCourseAdd: () => set({ courseAddOpen: false }),

  habitFormOpen: false,
  habitFormId: null,
  openHabitForm: (habitId = null) =>
    set({ habitFormOpen: true, habitFormId: habitId }),
  closeHabitForm: () => set({ habitFormOpen: false, habitFormId: null }),

  habitStatsOpenFor: null,
  openHabitStats: (habitId) => set({ habitStatsOpenFor: habitId }),
  closeHabitStats: () => set({ habitStatsOpenFor: null }),

  catalogFormOpen: false,
  catalogFormId: null,
  openCatalogForm: (itemId = null) =>
    set({ catalogFormOpen: true, catalogFormId: itemId }),
  closeCatalogForm: () => set({ catalogFormOpen: false, catalogFormId: null }),

  carrefourCookiesOpen: false,
  openCarrefourCookies: () => set({ carrefourCookiesOpen: true }),
  closeCarrefourCookies: () => set({ carrefourCookiesOpen: false }),

  lafourcheCredentialsOpen: false,
  openLafourcheCredentials: () => set({ lafourcheCredentialsOpen: true }),
  closeLafourcheCredentials: () => set({ lafourcheCredentialsOpen: false }),


  drawerOpen: false,
  openDrawer: () => set({ drawerOpen: true }),
  closeDrawer: () => set({ drawerOpen: false }),

  settingsOpen: false,
  openSettings: () => set({ settingsOpen: true }),
  closeSettings: () => set({ settingsOpen: false }),

  notificationsOpen: false,
  openNotifications: () => set({ notificationsOpen: true }),
  closeNotifications: () => set({ notificationsOpen: false }),
}));
